import { apiConfig } from "~/configs/apiConfig";
import * as Sentry from "@sentry/remix";

interface RequestConfig {
  url: string;
  method: "GET" | "POST" | "PUT" | "DELETE";
  headers?: Record<string, string>;
  data?: any;
}

const request = async <T>(config: RequestConfig): Promise<T> => {
  const headers: Record<string, string> = {
    ...config.headers,
    "Content-Type": "application/json",
  };

  // In Remix with Vite, we can use import.meta.env.SSR to detect server environment
  const baseUrl = import.meta.env.SSR
    ? apiConfig.ServerApiUrl
    : apiConfig.ClientApiUrl;

  let response;
  try {
    response = await fetch(`${baseUrl}${config.url}`, {
      method: config.method,
      headers,
      body: config.data ? JSON.stringify(config.data) : undefined,
    });
  } catch (e: any) {
    // Function name sets event name in sentry
    Sentry.withScope(function UnhandledApiErr(scope) {
      scope.setLevel("warning");
      scope.setTag("api_error", true);
      scope.setTag("status", e.status);
      scope.captureException(e);
    });
    throw e;
  }

  if (response.status === 404) {
    // Convert response body to text
    const errorText = await response.text();

    // Function name sets event name in sentry
    Sentry.withScope(function NotFoundApiError(scope) {
      scope.setLevel("warning");
      scope.setTag("api_error", true);
      scope.setTag("status", response.status);
      scope.setTag("response_body", errorText.replaceAll("\n", ""));
      scope.captureException("Error 404");
    });
    throw new Response(null, {
      status: 404,
      statusText: "Sorry the page you requested could not be found",
    });
  }

  if (!response.ok) {
    // Convert response body to text
    const errorText = await response.text();

    // Try parse the response body as json, otherwise use some defaults for error information
    let parsedError;
    try {
      parsedError = JSON.parse(errorText);
      parsedError.status = response.status;
    } catch {
      parsedError = {
        errorType: "INTERNAL_SERVER_ERROR",
        userMessage: errorText,
        info: null,
        status: response.status,
      };
    }

    // Function name sets event name in sentry
    Sentry.withScope(function UnexpectedApiError(scope) {
      scope.setLevel("warning");
      scope.setTag("api_error", true);
      scope.setTag("status", response.status);
      scope.setTag("info", parsedError.info);
      scope.setTag("user_message", parsedError.userMessage);
      scope.setTag("error_type", parsedError.errorType);
      scope.setTag("response_body", errorText.replaceAll("\n", ""));
      scope.setContext("Parsed Error", parsedError);
      scope.captureException(parsedError.errorType);
    });

    // Throw the API error
    throw parsedError;
  }

  const hasBody =
    response.headers.get("content-length") !== "0" &&
    response.headers.get("content-type")?.includes("application/json");

  const data = hasBody ? await response.json() : null;
  return data ? data.data : data;
};

const instance = {
  get: <T>(
    url: string,
    config: Omit<RequestConfig, "url" | "method"> = {}
  ): Promise<T> => {
    return request({ url, method: "GET", ...config });
  },

  post: <T>(
    url: string,
    data: any,
    config: Omit<RequestConfig, "url" | "method" | "data"> = {}
  ): Promise<T> => {
    return request({ url, method: "POST", data, ...config });
  },

  put: <T>(
    url: string,
    data: any,
    config: Omit<RequestConfig, "url" | "method" | "data"> = {}
  ): Promise<T> => {
    return request({ url, method: "PUT", data, ...config });
  },
};

export default instance;
